import { useMemo, useState } from 'react';
import { Card } from './ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { ChevronDown, ChevronRight, TrendingUp, Receipt, Download, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';
import { getBackendUrl } from '../lib/api';
import { getFiscalYear, getFiscalQuarter, hasCrewSuper } from '../lib/regionConfig';
import { computeJobFinancials, getCompletionDate } from '../lib/jobFinancials';

function csvEscape(value) {
  const str = value == null ? '' : String(value);
  if (/[",\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

function n2(num) {
  return (Math.round((num || 0) * 100) / 100).toFixed(2);
}

function buildFYCsv(fy, taxName) {
  const taxLabel = `${taxName} collected`;
  const rows = [];
  rows.push(['Financial Year', 'Quarter', 'Job #', 'Job Name', 'Client', 'Completed', 'Value (ex tax)', taxLabel, 'Profit (ex costs)']);

  fy.quarters.forEach((q) => {
    q.jobs.forEach((job) => {
      rows.push([
        fy.label,
        `${q.label}${q.months ? ` (${q.months})` : ''}`,
        job.job_number || '',
        job.project_name || 'Untitled Job',
        job.client || '',
        job._completedDate ? formatDate(job._completedDate) : '',
        n2(job._jobValue),
        n2(job._gst),
        n2(job._profit),
      ]);
    });
    // Quarter subtotal
    rows.push([fy.label, `${q.label} Total`, '', '', '', '', n2(q.jobValue), n2(q.gst), n2(q.profit)]);
  });
  // FY total
  rows.push([`${fy.label} Total`, '', '', '', '', '', n2(fy.jobValue), n2(fy.gst), n2(fy.profit)]);

  return rows.map((r) => r.map(csvEscape).join(',')).join('\n');
}

function downloadCsv(filename, content) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function CompletedJobsBreakdown({ jobs, region, formatCurrency, taxName = 'GST' }) {
  // Build nested structure: FY -> Quarter -> jobs, with aggregated totals
  const fiscalYears = useMemo(() => {
    const fyMap = new Map();

    jobs.forEach((job) => {
      const dateStr = getCompletionDate(job);
      const fy = dateStr ? getFiscalYear(dateStr, region) : null;
      const q = dateStr ? getFiscalQuarter(dateStr, region) : null;

      const fyKey = fy ? fy.key : 'unknown';
      const fyLabel = fy ? fy.label : 'Undated';
      const qNumber = q ? q.number : 0;
      const qLabel = q ? q.label : 'Undated';
      const qMonths = q ? q.monthsLabel : '';

      if (!fyMap.has(fyKey)) {
        fyMap.set(fyKey, {
          key: fyKey,
          label: fyLabel,
          sortKey: fy ? fy.startYear : -1,
          jobValue: 0,
          profit: 0,
          gst: 0,
          count: 0,
          quarters: new Map(),
        });
      }
      const fyEntry = fyMap.get(fyKey);

      if (!fyEntry.quarters.has(qNumber)) {
        fyEntry.quarters.set(qNumber, {
          number: qNumber,
          label: qLabel,
          months: qMonths,
          jobValue: 0,
          profit: 0,
          gst: 0,
          jobs: [],
          superJobIds: [],
        });
      }
      const qEntry = fyEntry.quarters.get(qNumber);

      const { jobValue, profit, gst } = computeJobFinancials(job);
      const enriched = { ...job, _jobValue: jobValue, _profit: profit, _gst: gst, _completedDate: dateStr };

      qEntry.jobs.push(enriched);
      qEntry.jobValue += jobValue;
      qEntry.profit += profit;
      qEntry.gst += gst;

      // Track jobs that have crew superannuation (crew labour → super summary is meaningful)
      const superAmount = job.crew_superannuation?.total_amount || 0;
      if (superAmount > 0) {
        qEntry.superJobIds.push(job.id);
      }

      fyEntry.jobValue += jobValue;
      fyEntry.profit += profit;
      fyEntry.gst += gst;
      fyEntry.count += 1;
    });

    // Convert to sorted arrays: FY descending (newest first), quarters ascending (Q1 -> Q4)
    return Array.from(fyMap.values())
      .sort((a, b) => b.sortKey - a.sortKey)
      .map((fy) => ({
        ...fy,
        quarters: Array.from(fy.quarters.values()).sort((a, b) => a.number - b.number),
      }));
  }, [jobs, region]);

  // Most recent FY expanded by default
  const [collapsedFYs, setCollapsedFYs] = useState(() => {
    const set = new Set();
    return set;
  });

  const [downloadingSuperKey, setDownloadingSuperKey] = useState(null);

  const handleDownloadQuarterSuper = async (fy, q) => {
    const key = `${fy.key}-${q.number}`;
    setDownloadingSuperKey(key);
    try {
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('session_token');
      const periodLabel = `${fy.label} · ${q.label}${q.months ? ` (${q.months})` : ''}`;
      const res = await fetch(`${backendUrl}/api/super-summary-pdf/compiled`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ job_ids: q.superJobIds, period_label: periodLabel }),
      });
      if (!res.ok) throw new Error('Failed to generate PDF');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Super_Summary_${fy.label.replace(/[^0-9A-Za-z]+/g, '-')}-${q.label}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Superannuation summary downloaded');
    } catch {
      toast.error('Failed to download superannuation summary');
    } finally {
      setDownloadingSuperKey(null);
    }
  };

  const toggleFY = (key) => {
    setCollapsedFYs((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const isFYCollapsed = (fy, index) => {
    // Default: first (most recent) FY open, rest closed — unless user toggled
    if (collapsedFYs.has(fy.key)) return true;
    if (collapsedFYs.has(`__open_${fy.key}`)) return false;
    return index !== 0;
  };

  const handleToggle = (fy, index) => {
    const currentlyCollapsed = isFYCollapsed(fy, index);
    setCollapsedFYs((prev) => {
      const next = new Set(prev);
      // Clear both markers then set the desired one
      next.delete(fy.key);
      next.delete(`__open_${fy.key}`);
      if (currentlyCollapsed) {
        next.add(`__open_${fy.key}`);
      } else {
        next.add(fy.key);
      }
      return next;
    });
  };

  return (
    <div className="space-y-4" data-testid="completed-fy-breakdown">
      {fiscalYears.map((fy, fyIndex) => {
        const collapsed = isFYCollapsed(fy, fyIndex);
        return (
          <Card key={fy.key} className="overflow-hidden bg-surface rounded-tile" data-testid={`fy-card-${fy.key}`}>
            {/* FY Header */}
            <div className={`w-full flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 sm:p-6 ${collapsed ? '' : 'border-b border-line'}`}>
              <button
                type="button"
                onClick={() => handleToggle(fy, fyIndex)}
                className="flex items-center gap-3 text-left hover:opacity-80 transition-opacity"
                data-testid={`fy-toggle-${fy.key}`}
              >
                {collapsed ? (
                  <ChevronRight className="w-5 h-5 text-muted-foreground shrink-0" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-muted-foreground shrink-0" />
                )}
                <div>
                  <h3 className="section-title">{fy.label}</h3>
                  <p className="text-sm text-muted-foreground">
                    {fy.count} {fy.count === 1 ? 'job' : 'jobs'} completed
                  </p>
                </div>
              </button>
              <div className="flex flex-col gap-3 pl-8 sm:pl-0 sm:flex-row sm:items-center sm:gap-6">
                <div className="grid grid-cols-2 gap-4 sm:flex sm:items-center sm:gap-6">
                  <div className="text-left sm:text-right">
                    <p className="text-xs text-muted-foreground flex items-center gap-1 sm:justify-end">
                      <Receipt className="w-3 h-3" /> {taxName} collected
                    </p>
                    <p className="text-lg font-light tabular-nums">{formatCurrency(fy.gst)}</p>
                  </div>
                  <div className="text-left sm:text-right">
                    <p className="text-xs text-muted-foreground flex items-center gap-1 sm:justify-end">
                      <TrendingUp className="w-3 h-3" /> Profit (ex costs)
                    </p>
                    <p className={`text-lg font-light tabular-nums ${fy.profit >= 0 ? 'text-moss' : 'text-brick'}`}>
                      {formatCurrency(fy.profit)}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => downloadCsv(`completed-jobs-${fy.label.replace(/[^0-9A-Za-z]+/g, '-')}.csv`, buildFYCsv(fy, taxName))}
                  className="inline-flex items-center justify-center gap-1.5 rounded-pill border border-line-strong bg-transparent px-4 py-1.5 text-sm text-foreground hover:bg-mist transition-colors w-full sm:w-auto"
                  data-testid={`fy-export-csv-${fy.key}`}
                  title="Export this financial year to CSV"
                >
                  <Download className="w-3.5 h-3.5" /> Export CSV
                </button>
              </div>
            </div>

            {/* Quarters */}
            {!collapsed && (
              <div className="divide-y divide-line" data-testid={`fy-body-${fy.key}`}>
                {fy.quarters.map((q) => (
                  <div key={q.number} data-testid={`quarter-${fy.key}-${q.number}`}>
                    {/* Quarter header */}
                    <div className="flex flex-col gap-3 px-4 py-3 sm:px-6 bg-surface sm:flex-row sm:items-center sm:justify-between sm:gap-2">
                      <div className="flex items-baseline gap-2">
                        <span className="text-base">{q.label}</span>
                        {q.months && (
                          <span className="text-xs text-muted-foreground">{q.months}</span>
                        )}
                        <span className="text-xs text-muted-foreground">
                          · {q.jobs.length} {q.jobs.length === 1 ? 'job' : 'jobs'}
                        </span>
                      </div>
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-6">
                        <div className="grid grid-cols-3 gap-2 sm:flex sm:items-center sm:gap-6">
                          <div className="sm:flex sm:items-center sm:gap-2">
                            <span className="block text-xs text-muted-foreground">Value</span>
                            <span className="text-sm tabular-nums">{formatCurrency(q.jobValue)}</span>
                          </div>
                          <div className="sm:flex sm:items-center sm:gap-2">
                            <span className="block text-xs text-muted-foreground">{taxName} collected</span>
                            <span className="text-sm tabular-nums">{formatCurrency(q.gst)}</span>
                          </div>
                          <div className="sm:flex sm:items-center sm:gap-2">
                            <span className="block text-xs text-muted-foreground">Profit (ex costs)</span>
                            <span className={`text-sm tabular-nums ${q.profit >= 0 ? 'text-moss' : 'text-brick'}`}>
                              {formatCurrency(q.profit)}
                            </span>
                          </div>
                        </div>
                        {hasCrewSuper(region) && q.superJobIds.length > 0 && (
                          <button
                            type="button"
                            onClick={() => handleDownloadQuarterSuper(fy, q)}
                            disabled={downloadingSuperKey === `${fy.key}-${q.number}`}
                            className="inline-flex items-center justify-center gap-1.5 rounded-pill border border-line-strong bg-transparent px-4 py-1.5 text-sm text-foreground hover:bg-mist transition-colors disabled:opacity-45 disabled:cursor-not-allowed w-full sm:w-auto"
                            data-testid={`quarter-super-pdf-${fy.key}-${q.number}`}
                            title="Compile all crew superannuation summaries for this quarter into one PDF"
                          >
                            {downloadingSuperKey === `${fy.key}-${q.number}` ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <FileText className="w-3.5 h-3.5" />
                            )}
                            Super summary PDF
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Jobs table */}
                    <div className="px-1 sm:px-0">
                    <Table className="table-fixed w-full">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="pl-4 w-[60px] sm:w-[110px] lg:w-[140px] whitespace-nowrap">
                            <span className="sm:hidden">#</span>
                            <span className="hidden sm:inline">Job #</span>
                          </TableHead>
                          <TableHead className="min-w-0">Job name</TableHead>
                          <TableHead className="hidden sm:table-cell w-[130px] lg:w-[180px]">Client</TableHead>
                          <TableHead className="text-right w-[84px] sm:w-[110px] lg:w-[130px]">
                            Value<span className="hidden lg:inline"> (ex {taxName})</span>
                          </TableHead>
                          <TableHead className="text-right w-[84px] sm:w-[120px] lg:w-[140px]">
                            {taxName}<span className="hidden lg:inline"> collected</span>
                          </TableHead>
                          <TableHead className="text-right hidden md:table-cell w-[120px] lg:w-[140px]">
                            Profit<span className="hidden lg:inline"> (ex costs)</span>
                          </TableHead>
                          <TableHead className="text-right pr-4 hidden lg:table-cell w-[120px]">Completed</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {q.jobs.map((job) => (
                          <TableRow
                            key={job.id}
                            className="cursor-pointer hover:bg-mist"
                            onClick={() => (window.location.href = `/jobs/${job.id}`)}
                            data-testid={`completed-job-row-${job.id}`}
                          >
                            <TableCell className="pl-4 tabular-nums text-xs sm:text-sm text-muted-foreground truncate">
                              <span className="block truncate" title={job.job_number || ''}>{job.job_number || '—'}</span>
                            </TableCell>
                            <TableCell className="overflow-hidden">
                              <p className="text-sm truncate" title={job.project_name || 'Untitled Job'}>
                                {job.project_name || 'Untitled Job'}
                              </p>
                            </TableCell>
                            <TableCell className="hidden sm:table-cell overflow-hidden">
                              <span className="block text-sm text-muted-foreground truncate" title={job.client || ''}>
                                {job.client || '—'}
                              </span>
                            </TableCell>
                            <TableCell className="text-right tabular-nums text-xs sm:text-sm whitespace-nowrap">
                              {formatCurrency(job._jobValue)}
                            </TableCell>
                            <TableCell className="text-right tabular-nums text-xs sm:text-sm whitespace-nowrap">
                              {formatCurrency(job._gst)}
                            </TableCell>
                            <TableCell className={`text-right tabular-nums text-sm hidden md:table-cell whitespace-nowrap ${job._profit >= 0 ? 'text-moss' : 'text-brick'}`}>
                              {formatCurrency(job._profit)}
                            </TableCell>
                            <TableCell className="text-right text-sm text-muted-foreground pr-4 hidden lg:table-cell whitespace-nowrap">
                              {job._completedDate ? formatDate(job._completedDate) : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}
